import { AccredibleObjectWithId } from '@accredible-frontend-v2/models';
import { AccredibleButtonComponentModule } from '@accredible-frontend-v2/new-components/button';
import { AccredibleIconComponentModule } from '@accredible-frontend-v2/new-components/icon';
import { AccredibleMultiSearchFilterMenuModule } from '@accredible-frontend-v2/new-components/multi-search-filter-menu';
import { AccredibleTextInputModule } from '@accredible-frontend-v2/new-components/text-input';
import { AccredibleLanguageServiceModule } from '@accredible-frontend-v2/services/language';
import {
  AccredibleSidenavService,
  AccredibleSidenavServiceModule,
} from '@accredible-frontend-v2/services/sidenav';
import { ChangeDetectionStrategy, Component, effect, inject, input, output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { SkillCategoriesService } from '../services/skill-categories.service';
import { SkillCategoriesMobileFilterComponent } from '../skill-category-mobile-filter/skill-categories-mobile-filter.component';

@Component({
  standalone: true,
  selector: 'dod-directory-filtering',
  templateUrl: `./directory-filtering.component.html`,
  styleUrls: ['./directory-filtering.component.scss'],
  imports: [
    TranslocoModule,
    AccredibleIconComponentModule,
    AccredibleButtonComponentModule,
    AccredibleSidenavServiceModule,
    AccredibleMultiSearchFilterMenuModule,
    AccredibleTextInputModule,
    AccredibleLanguageServiceModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DirectoryFilteringComponent {
  private readonly _sidenav = inject(AccredibleSidenavService);
  private readonly _skillCategories = inject(SkillCategoriesService);

  search = input<string>('');
  categories = input<string[]>([]);

  readonly skillCategories = this._skillCategories.skillCategories;
  readonly selectedSkillCategories = this._skillCategories.selectedSkillCategories;

  onSearch = output<string>();
  onSkillCategoryUpdated = output<string[]>();

  searchControl = new FormControl<string>(this.search());

  constructor() {
    effect(() => {
      const currentSearch = this.search();
      if (currentSearch !== this.searchControl.value) {
        this.searchControl.setValue(currentSearch, { emitEvent: false });
      }
    });

    effect(
      () => {
        const categoryKeys = this.categories();
        const all = this._skillCategories.allCategories();

        if (categoryKeys?.length && all?.length) {
          // Match by id or by name
          const matches = all.filter(
            (sc) =>
              categoryKeys.includes(sc.id as string) || categoryKeys.includes(sc.name as string),
          );

          if (matches.length) {
            this._skillCategories.updateSelected(matches);
          }
        }
      },
      { allowSignalWrites: true },
    );
  }

  // IOS cannot handle (keyup.enter) directly, so we need to handle it manually
  keyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  }

  handleSearch(): void {
    this.onSearch.emit(this.searchControl.value ?? '');
  }

  selectedSkillCategoriesChange(newSelection: AccredibleObjectWithId[]): void {
    this._skillCategories.updateSelected(newSelection);

    this.onSkillCategoryUpdated.emit(newSelection.map((obj) => obj.name as string));
  }

  clear(): void {
    this.searchControl.reset();
    this.selectedSkillCategoriesChange([]);
    this.handleSearch();
  }

  clearSearchTerm(): void {
    this.searchControl.setValue('');
    this.handleSearch();
  }

  openMobileSidenav(): void {
    this._sidenav
      .open(SkillCategoriesMobileFilterComponent, {
        width: '100%',
      })
      .afterClosed()
      .subscribe((data: { selectedSkillCategories?: AccredibleObjectWithId[] }) => {
        this.selectedSkillCategoriesChange(data.selectedSkillCategories);
      });
  }

  searchCategory(query: string): void {
    this._skillCategories.filterBy(query);
  }
}
